import { useEffect, useRef, useState } from 'react';
import {
  Plus,
  Trash2,
  Edit3,
  Play,
  ChevronDown,
  ChevronRight,
  BookOpen,
} from 'lucide-react';
import { useFlashcardDecks, useFlashcardsInDeck } from '../../../hooks/useFlashcards';
import type { FlashcardDeckDto } from '../../../api/flashcardDecksService';
import type { FlashcardDto } from '../../../api/flashcardsService';
import { useAlert } from '../../../contexts/AlertContext';
import FlashcardLibraryTab from './FlashcardLibraryTab';
import StudyScopeDialog from './StudyScopeDialog';
import {
  primaryButtonStyle,
  secondaryButtonStyle,
  iconButtonStyle,
  compactInputStyle,
  compactTextareaStyle,
  fieldLabelStyle,
} from './controls';
import { useTranslation } from '../../../i18n/useTranslation';

/**
 * Detect the card type from its stored payload.
 *
 * Library-picked cards encode MCQ options as JSON in `answer` and use
 * `{{c1::...}}` markers in `question` for Cloze. Hand-typed cards have
 * neither, so they fall through to 'QA'. Matches the logic in
 * FlashcardStudyMode.parsePayload so list + study agree.
 */
function detectCardType(card: { question: string; answer: string }): 'QA' | 'Cloze' | 'MCQ' {
  try {
    const j = JSON.parse(card.answer);
    if (j && typeof j === 'object' && typeof j.correct === 'string' && Array.isArray(j.distractors)) {
      return 'MCQ';
    }
  } catch { /* not JSON */ }
  if (/\{\{c\d+::[^}]+\}\}/.test(card.question)) return 'Cloze';
  return 'QA';
}

/** Display form of the question in the list — strip Cloze markers. */
function renderQuestionForList(question: string): string {
  return question.replace(/\{\{c\d+::([^}]+)\}\}/g, '_____');
}

interface FlashcardPanelProps {
  bookId: string;
  currentLocation: string | number;
  currentLocationLabel: string;
  onStartStudy: (deckId: string, deckName?: string, chapterFilter?: string | null) => void;
  /**
   * Active chapter's SecureContentChunkId. When null/undefined, the Library tab
   * shows a placeholder. Reader integration for live tracking is follow-up scope.
   */
  secureContentChunkId?: string | null;
}

export default function FlashcardPanel({
  bookId,
  currentLocation,
  currentLocationLabel,
  onStartStudy,
  secureContentChunkId,
}: FlashcardPanelProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'deck' | 'library'>('deck');
  const { decks, createDeck, updateDeck, deleteDeck, isLoading } = useFlashcardDecks(bookId);
  const { confirm, showToast } = useAlert();

  const [expandedDeckId, setExpandedDeckId] = useState<string | null>(null);

  // Pending-study state: when the user taps Study on a deck, we first open the
  // scope dialog. Only after they pick "All" or a specific chapter do we
  // hand off to the host-level study mode.
  const [pendingStudy, setPendingStudy] = useState<{ id: string; name: string } | null>(null);
  const requestStudy = (deckId: string, deckName: string) => {
    setPendingStudy({ id: deckId, name: deckName });
  };

  // Inline Add Deck state
  const [isAddingDeck, setIsAddingDeck] = useState(false);
  const [newDeckName, setNewDeckName] = useState('');
  const [newDeckDescription, setNewDeckDescription] = useState('');
  const [isSavingDeck, setIsSavingDeck] = useState(false);
  const addDeckCardRef = useRef<HTMLDivElement>(null);
  const addDeckInputRef = useRef<HTMLInputElement>(null);

  // Inline Edit Deck state
  const [editingDeckId, setEditingDeckId] = useState<string | null>(null);
  const [editDeckName, setEditDeckName] = useState('');
  const [editDeckDescription, setEditDeckDescription] = useState('');

  useEffect(() => {
    if (isAddingDeck) {
      addDeckCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      addDeckInputRef.current?.focus();
    }
  }, [isAddingDeck]);

  // Fetch flashcards for expanded deck
  const {
    flashcards,
    createFlashcard,
    updateFlashcard,
    deleteFlashcard,
    isLoading: isLoadingCards,
  } = useFlashcardsInDeck(expandedDeckId);

  const handleStartAddDeck = () => {
    setIsAddingDeck(true);
    setNewDeckName('');
    setNewDeckDescription('');
  };

  const handleSaveAddDeck = async () => {
    if (!newDeckName.trim()) return;
    setIsSavingDeck(true);
    try {
      await createDeck({
        bookId,
        name: newDeckName.trim(),
        description: newDeckDescription.trim() || undefined,
      });
      setIsAddingDeck(false);
      setNewDeckName('');
      setNewDeckDescription('');
    } catch (error: any) {
      console.error('Failed to create deck:', error);
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        t('App.Reader.Flashcard.CreateDeckError');
      showToast(msg, { type: 'error' });
    } finally {
      setIsSavingDeck(false);
    }
  };

  const handleCancelAddDeck = () => {
    setIsAddingDeck(false);
    setNewDeckName('');
    setNewDeckDescription('');
  };

  const handleStartEditDeck = (deck: FlashcardDeckDto) => {
    setEditingDeckId(deck.id);
    setEditDeckName(deck.name);
    setEditDeckDescription(deck.description || '');
  };

  const handleSaveEditDeck = async (deckId: string) => {
    if (!editDeckName.trim()) return;
    try {
      await updateDeck({
        id: deckId,
        input: {
          name: editDeckName.trim(),
          description: editDeckDescription.trim() || undefined,
        },
      });
      setEditingDeckId(null);
      setEditDeckName('');
      setEditDeckDescription('');
    } catch (error: any) {
      console.error('Failed to update deck:', error);
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        t('App.Reader.Flashcard.UpdateDeckError');
      showToast(msg, { type: 'error' });
    }
  };

  const handleCancelEditDeck = () => {
    setEditingDeckId(null);
    setEditDeckName('');
    setEditDeckDescription('');
  };

  const handleDeleteDeck = async (deckId: string) => {
    const ok = await confirm({
      title: t('App.Reader.Flashcard.DeleteConfirmTitle'),
      message: t('App.Reader.Flashcard.DeleteConfirmMessage'),
      confirmText: t('App.Reader.Flashcard.DeleteDeckLabel'),
      variant: 'danger',
    });
    if (ok) {
      await deleteDeck(deckId);
      if (expandedDeckId === deckId) {
        setExpandedDeckId(null);
      }
    }
  };

  const handleToggleDeck = (deckId: string) => {
    setExpandedDeckId(expandedDeckId === deckId ? null : deckId);
  };

  const handleDeleteFlashcard = async (cardId: string) => {
    const ok = await confirm({
      title: t('App.Reader.Flashcard.DeleteCardLabel'),
      message: t('App.Reader.Flashcard.DeleteConfirmMessage'),
      confirmText: t('App.Reader.Flashcard.DeleteCardLabel'),
      variant: 'danger',
    });
    if (ok) {
      await deleteFlashcard(cardId);
    }
  };

  // Pick a deck to use as the library "current deck" target. Prefer the expanded deck,
  // otherwise fall back to the first available deck. Null if the user has none yet.
  const libraryTargetDeckId = expandedDeckId ?? (decks.length > 0 ? decks[0].id : null);

  return (
    <div className="h-full flex flex-col" style={{ width: '100%' }}>
      {/* Tabs */}
      <div className="flex border-b border-neutral-200" role="tablist" aria-label="Flashcards view">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'deck'}
          className={`flex-1 min-h-[44px] text-sm transition-colors ${
            activeTab === 'deck'
              ? 'border-b-2 border-primary-600 text-primary-700 font-medium'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
          onClick={() => setActiveTab('deck')}
        >
          {t('App.Reader.Flashcard.MyDeckTab')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'library'}
          className={`flex-1 min-h-[44px] text-sm transition-colors ${
            activeTab === 'library'
              ? 'border-b-2 border-primary-600 text-primary-700 font-medium'
              : 'text-neutral-600 hover:text-neutral-800'
          }`}
          onClick={() => setActiveTab('library')}
        >
          {t('App.Reader.Flashcard.LibraryTab')}
        </button>
      </div>

      {activeTab === 'library' && (
        <div className="flex-1 min-h-0">
          <FlashcardLibraryTab
            bookId={bookId}
            secureContentChunkId={secureContentChunkId ?? null}
          />
        </div>
      )}

      {activeTab === 'deck' && (isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">{t('App.Reader.Flashcard.LoadingDecks')}</div>
        </div>
      ) : (
      <div className="flex-1 min-h-0 flex flex-col" style={{ padding: '20px' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-neutral-600">
          {t('App.Reader.Flashcard.DeckCount', { count: decks.length })}
        </p>
        <button
          onClick={handleStartAddDeck}
          disabled={isAddingDeck}
          style={{
            ...primaryButtonStyle,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            opacity: isAddingDeck ? 0.6 : 1,
            cursor: isAddingDeck ? 'not-allowed' : 'pointer',
          }}
          title={t('App.Reader.Flashcard.CreateDeck')}
          aria-label={t('App.Reader.Flashcard.CreateDeck')}
        >
          <Plus className="w-[14px] h-[14px]" aria-hidden="true" />
          {t('App.Reader.Flashcard.CreateDeck')}
        </button>
      </div>

      {/* Deck List */}
      <div className="flex-1 min-h-0 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Inline Add Deck Card */}
        {isAddingDeck && (
          <div
            ref={addDeckCardRef}
            style={{
              padding: '10px 12px',
              marginBottom: '8px',
              borderLeft: '4px solid rgb(var(--color-primary-500))',
              borderRadius: '6px',
              background: 'var(--color-surface)',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
              border: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                marginBottom: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {t('App.Reader.Flashcard.NewDeckLabel')}
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={fieldLabelStyle}>{t('App.Reader.Flashcard.NameLabel')}</label>
              <input
                ref={addDeckInputRef}
                type="text"
                value={newDeckName}
                onChange={(e) => setNewDeckName(e.target.value)}
                placeholder={t('App.Reader.Flashcard.NamePlaceholder')}
                style={compactInputStyle}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCancelAddDeck();
                }}
              />
            </div>

            <div>
              <label style={fieldLabelStyle}>{t('App.Reader.Flashcard.DescriptionLabel')}</label>
              <textarea
                value={newDeckDescription}
                onChange={(e) => setNewDeckDescription(e.target.value)}
                placeholder={t('App.Reader.Flashcard.DescriptionPlaceholder')}
                rows={2}
                style={compactTextareaStyle}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCancelAddDeck();
                }}
              />
            </div>
            <div style={{ marginTop: '8px', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleSaveAddDeck}
                disabled={!newDeckName.trim() || isSavingDeck}
                aria-label={t('App.Reader.Flashcard.Save')}
                style={{
                  ...primaryButtonStyle,
                  opacity: !newDeckName.trim() || isSavingDeck ? 0.5 : 1,
                  cursor: !newDeckName.trim() || isSavingDeck ? 'not-allowed' : 'pointer',
                }}
              >
                {isSavingDeck ? t('App.Reader.Flashcard.Saving') : t('App.Reader.Flashcard.Save')}
              </button>
              <button
                onClick={handleCancelAddDeck}
                disabled={isSavingDeck}
                aria-label={t('App.Reader.Flashcard.CancelAddDeckLabel')}
                style={secondaryButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {decks.length === 0 && !isAddingDeck ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <BookOpen className="w-16 h-16 text-neutral-300 mb-4" />
            <p className="text-neutral-500 text-sm mb-4">
              {t('App.Reader.Flashcard.EmptyDescription')}
            </p>
            <button
              onClick={handleStartAddDeck}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              {t('App.Reader.Flashcard.CreateFirstButton')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {decks.map((deck) => (
              <DeckItem
                key={deck.id}
                deck={deck}
                isExpanded={expandedDeckId === deck.id}
                isEditing={editingDeckId === deck.id}
                editName={editDeckName}
                editDescription={editDeckDescription}
                flashcards={expandedDeckId === deck.id ? flashcards : []}
                isLoadingCards={isLoadingCards}
                currentLocation={currentLocation}
                currentLocationLabel={currentLocationLabel}
                onToggle={() => handleToggleDeck(deck.id)}
                onStartEdit={() => handleStartEditDeck(deck)}
                onSaveEdit={() => handleSaveEditDeck(deck.id)}
                onCancelEdit={handleCancelEditDeck}
                onEditNameChange={setEditDeckName}
                onEditDescriptionChange={setEditDeckDescription}
                onDelete={() => handleDeleteDeck(deck.id)}
                onStartStudy={(deckName) => requestStudy(deck.id, deckName ?? deck.name)}
                onCreateCard={createFlashcard}
                onUpdateCard={updateFlashcard}
                onDeleteCard={handleDeleteFlashcard}
              />
            ))}
          </div>
        )}
      </div>
      </div>
      ))}

      {/* Pre-study scope picker — choose whole deck vs. a single chapter. */}
      {pendingStudy && (
        <StudyScopeDialog
          open={true}
          deckId={pendingStudy.id}
          deckName={pendingStudy.name}
          onCancel={() => setPendingStudy(null)}
          onStart={(chapterFilter) => {
            const { id, name } = pendingStudy;
            setPendingStudy(null);
            onStartStudy(id, name, chapterFilter);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deck Item Component
// ---------------------------------------------------------------------------

interface DeckItemProps {
  deck: FlashcardDeckDto;
  isExpanded: boolean;
  isEditing: boolean;
  editName: string;
  editDescription: string;
  flashcards: FlashcardDto[];
  isLoadingCards: boolean;
  currentLocation: string | number;
  currentLocationLabel: string;
  onToggle: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditNameChange: (value: string) => void;
  onEditDescriptionChange: (value: string) => void;
  onDelete: () => void;
  onStartStudy: (deckName: string) => void;
  onCreateCard: (input: {
    deckId: string;
    question: string;
    answer: string;
    location: string;
    locationLabel: string;
  }) => Promise<any>;
  onUpdateCard: (args: { id: string; input: { question: string; answer: string } }) => Promise<any>;
  onDeleteCard: (cardId: string) => void;
}

function DeckItem({
  deck,
  isExpanded,
  isEditing,
  editName,
  editDescription,
  flashcards,
  isLoadingCards,
  currentLocation,
  currentLocationLabel,
  onToggle,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditNameChange,
  onEditDescriptionChange,
  onDelete,
  onStartStudy,
  onCreateCard,
  onUpdateCard,
  onDeleteCard,
}: DeckItemProps) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        borderLeft: '4px solid rgb(var(--color-primary-500))',
        borderRadius: '6px',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden',
      }}
    >
      {/* Deck Header */}
      <div style={{ padding: '10px 12px' }}>
        {isEditing ? (
          <div>
            <div style={{ marginBottom: '10px' }}>
              <label style={fieldLabelStyle}>{t('App.Reader.Flashcard.NameLabel')}</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => onEditNameChange(e.target.value)}
                placeholder={t('App.Reader.Flashcard.NamePlaceholder')}
                autoFocus
                style={compactInputStyle}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') onCancelEdit();
                  if (e.key === 'Enter' && editName.trim()) onSaveEdit();
                }}
              />
            </div>
            <div>
              <label style={fieldLabelStyle}>{t('App.Reader.Flashcard.DescriptionLabel')}</label>
              <textarea
                value={editDescription}
                onChange={(e) => onEditDescriptionChange(e.target.value)}
                placeholder={t('App.Reader.Flashcard.DescriptionPlaceholder')}
                rows={2}
                style={compactTextareaStyle}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') onCancelEdit();
                }}
              />
            </div>
            <div style={{ marginTop: '8px', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={onSaveEdit}
                disabled={!editName.trim()}
                aria-label={t('App.Reader.Flashcard.Save')}
                style={{
                  ...primaryButtonStyle,
                  opacity: !editName.trim() ? 0.5 : 1,
                  cursor: !editName.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {t('App.Reader.Flashcard.Save')}
              </button>
              <button
                onClick={onCancelEdit}
                aria-label={t('App.Reader.Flashcard.EditDeckLabel')}
                style={secondaryButtonStyle}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <button
              onClick={onToggle}
              style={{
                marginTop: '2px',
                padding: 0,
                background: 'none',
                border: 'none',
                color: 'var(--color-text-tertiary)',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              aria-label={isExpanded ? t('App.Reader.Flashcard.CollapseDeck') : t('App.Reader.Flashcard.ExpandDeck')}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
              ) : (
                <ChevronRight className="w-4 h-4" aria-hidden="true" />
              )}
            </button>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {deck.name}
              </div>
              {deck.description && (
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-text-secondary)',
                    marginTop: '2px',
                    lineHeight: 1.4,
                  }}
                >
                  {deck.description}
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '4px',
                  fontSize: '0.75rem',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                <span>
                  {t('App.Reader.Flashcard.CardCount', { count: deck.cardCount })}
                </span>
                {deck.cardCount > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onStartStudy(deck.name);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: 'rgb(var(--color-primary-600))',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Play className="w-3 h-3" />
                    {t('App.Reader.Flashcard.Study')}
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStartEdit();
                }}
                style={iconButtonStyle}
                title={t('App.Reader.Flashcard.EditDeckLabel')}
                aria-label={t('App.Reader.Flashcard.EditDeckLabel')}
              >
                <Edit3 className="w-[14px] h-[14px]" aria-hidden="true" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                style={iconButtonStyle}
                title={t('App.Reader.Flashcard.DeleteDeckLabel')}
                aria-label={t('App.Reader.Flashcard.DeleteDeckLabel')}
              >
                <Trash2 className="w-[14px] h-[14px]" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && !isEditing && (
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--color-neutral-50)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <FlashcardList
            deckId={deck.id}
            flashcards={flashcards}
            isLoadingCards={isLoadingCards}
            currentLocation={currentLocation}
            currentLocationLabel={currentLocationLabel}
            onCreateCard={onCreateCard}
            onUpdateCard={onUpdateCard}
            onDeleteCard={onDeleteCard}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flashcard List (with inline add + inline edit)
// ---------------------------------------------------------------------------

interface FlashcardListProps {
  deckId: string;
  flashcards: FlashcardDto[];
  isLoadingCards: boolean;
  currentLocation: string | number;
  currentLocationLabel: string;
  onCreateCard: (input: {
    deckId: string;
    question: string;
    answer: string;
    location: string;
    locationLabel: string;
  }) => Promise<any>;
  onUpdateCard: (args: { id: string; input: { question: string; answer: string } }) => Promise<any>;
  onDeleteCard: (cardId: string) => void;
}

function FlashcardList({
  deckId,
  flashcards,
  isLoadingCards,
  currentLocation,
  currentLocationLabel,
  onCreateCard,
  onUpdateCard,
  onDeleteCard,
}: FlashcardListProps) {
  const { t } = useTranslation();
  const { showToast } = useAlert();

  // Inline Add
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [isSavingCard, setIsSavingCard] = useState(false);
  const addCardRef = useRef<HTMLDivElement>(null);

  // Inline Edit
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');

  useEffect(() => {
    if (isAddingCard) {
      addCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isAddingCard]);

  const handleStartAdd = () => {
    setIsAddingCard(true);
    setNewQuestion('');
    setNewAnswer('');
  };

  const handleSaveAdd = async () => {
    if (!newQuestion.trim() || !newAnswer.trim()) return;
    setIsSavingCard(true);
    try {
      await onCreateCard({
        deckId,
        question: newQuestion.trim(),
        answer: newAnswer.trim(),
        location: String(currentLocation),
        locationLabel: currentLocationLabel,
      });
      setIsAddingCard(false);
      setNewQuestion('');
      setNewAnswer('');
    } catch (error: any) {
      console.error('Failed to create flashcard:', error);
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        t('App.Reader.Flashcard.CreateCardError');
      showToast(msg, { type: 'error' });
    } finally {
      setIsSavingCard(false);
    }
  };

  const handleCancelAdd = () => {
    setIsAddingCard(false);
    setNewQuestion('');
    setNewAnswer('');
  };

  const handleStartEdit = (card: FlashcardDto) => {
    setEditingCardId(card.id);
    setEditQuestion(card.question);
    setEditAnswer(card.answer);
  };

  const handleSaveEdit = async (cardId: string) => {
    if (!editQuestion.trim() || !editAnswer.trim()) return;
    try {
      await onUpdateCard({
        id: cardId,
        input: { question: editQuestion.trim(), answer: editAnswer.trim() },
      });
      setEditingCardId(null);
      setEditQuestion('');
      setEditAnswer('');
    } catch (error: any) {
      console.error('Failed to update flashcard:', error);
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        t('App.Reader.Flashcard.UpdateCardError');
      showToast(msg, { type: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditingCardId(null);
    setEditQuestion('');
    setEditAnswer('');
  };

  return (
    <>
      {/* Add Flashcard trigger / inline form */}
      {!isAddingCard ? (
        <button
          onClick={handleStartAdd}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '0.813rem',
            color: 'rgb(var(--color-primary-600))',
            background: 'transparent',
            border: '1px dashed rgb(var(--color-primary-300))',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Plus className="w-4 h-4" />
          {t('App.Reader.Flashcard.AddCardButton')}
        </button>
      ) : (
        <div
          ref={addCardRef}
          style={{
            padding: '10px 12px',
            borderLeft: '3px solid rgb(var(--color-primary-500))',
            borderRadius: '4px',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              fontSize: '0.688rem',
              fontWeight: 600,
              color: 'var(--color-text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              marginBottom: '4px',
            }}
          >
            {t('App.Reader.Flashcard.QuestionLabel')}
          </div>
          <textarea
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            placeholder={t('App.Reader.Flashcard.QuestionPlaceholder')}
            rows={2}
            autoFocus
            style={compactTextareaStyle}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelAdd();
            }}
          />
          <div
            style={{
              fontSize: '0.688rem',
              fontWeight: 600,
              color: 'var(--color-text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              marginTop: '8px',
              marginBottom: '4px',
            }}
          >
            {t('App.Reader.Flashcard.AnswerLabel')}
          </div>
          <textarea
            value={newAnswer}
            onChange={(e) => setNewAnswer(e.target.value)}
            placeholder={t('App.Reader.Flashcard.AnswerPlaceholder')}
            rows={2}
            style={compactTextareaStyle}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelAdd();
            }}
          />
          <div style={{ marginTop: '8px', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleSaveAdd}
              disabled={!newQuestion.trim() || !newAnswer.trim() || isSavingCard}
              aria-label={t('App.Reader.Flashcard.Save')}
              style={{
                ...primaryButtonStyle,
                opacity:
                  !newQuestion.trim() || !newAnswer.trim() || isSavingCard ? 0.5 : 1,
                cursor:
                  !newQuestion.trim() || !newAnswer.trim() || isSavingCard
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              {isSavingCard ? t('App.Reader.Flashcard.Saving') : t('App.Reader.Flashcard.Save')}
            </button>
            <button
              onClick={handleCancelAdd}
              disabled={isSavingCard}
              aria-label="Cancel adding flashcard"
              style={secondaryButtonStyle}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Flashcard entries */}
      {isLoadingCards ? (
        <div
          style={{
            textAlign: 'center',
            padding: '16px 0',
            fontSize: '0.813rem',
            color: 'var(--color-text-tertiary)',
          }}
        >
          {t('App.Reader.Flashcard.LoadingCards')}
        </div>
      ) : flashcards.length > 0 ? (
        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {flashcards.map((card) =>
            editingCardId === card.id ? (
              <div
                key={card.id}
                style={{
                  padding: '10px 12px',
                  borderLeft: '3px solid rgb(var(--color-primary-500))',
                  borderRadius: '4px',
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.688rem',
                    fontWeight: 600,
                    color: 'var(--color-text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    marginBottom: '4px',
                  }}
                >
                  {t('App.Reader.Flashcard.QuestionLabel')}
                </div>
                <textarea
                  value={editQuestion}
                  onChange={(e) => setEditQuestion(e.target.value)}
                  rows={2}
                  autoFocus
                  style={compactTextareaStyle}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <div
                  style={{
                    fontSize: '0.688rem',
                    fontWeight: 600,
                    color: 'var(--color-text-tertiary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    marginTop: '8px',
                    marginBottom: '4px',
                  }}
                >
                  {t('App.Reader.Flashcard.AnswerLabel')}
                </div>
                <textarea
                  value={editAnswer}
                  onChange={(e) => setEditAnswer(e.target.value)}
                  rows={2}
                  style={compactTextareaStyle}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                />
                <div style={{ marginTop: '8px', display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleSaveEdit(card.id)}
                    disabled={!editQuestion.trim() || !editAnswer.trim()}
                    aria-label={t('App.Reader.Flashcard.Save')}
                    style={{
                      ...primaryButtonStyle,
                      opacity: !editQuestion.trim() || !editAnswer.trim() ? 0.5 : 1,
                      cursor:
                        !editQuestion.trim() || !editAnswer.trim() ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('App.Reader.Flashcard.Save')}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    aria-label="Cancel editing flashcard"
                    style={secondaryButtonStyle}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={card.id}
                style={{
                  padding: '8px 10px',
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Card-type badge (hidden for plain Q/A to avoid visual noise). */}
                    {(() => {
                      const cardType = detectCardType(card);
                      if (cardType === 'QA') return null;
                      const label = cardType === 'MCQ'
                        ? t('App.Reader.Flashcard.TypeMultipleChoice')
                        : t('App.Reader.Flashcard.TypeFillInBlank');
                      return (
                        <span
                          style={{
                            display: 'inline-block',
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: 'rgb(var(--color-primary-50))',
                            color: 'rgb(var(--color-primary-700))',
                            marginBottom: '4px',
                          }}
                        >
                          {label}
                        </span>
                      );
                    })()}
                    <p style={{ fontSize: '0.813rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                      {renderQuestionForList(card.question)}
                    </p>
                    {/* Answer intentionally hidden in the list to avoid spoiling study. */}
                    {/* For context on where in the book the card came from: */}
                    {card.locationLabel && (
                      <p style={{ fontSize: '0.688rem', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>
                        {card.locationLabel}
                      </p>
                    )}
                    {card.lastReviewedAt && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          marginTop: '4px',
                          fontSize: '0.688rem',
                        }}
                      >
                        <span style={{ color: 'rgb(var(--color-success-600))' }}>
                          ✓ {card.correctCount}
                        </span>
                        <span style={{ color: 'rgb(var(--color-error-600))' }}>
                          ✗ {card.incorrectCount}
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                    <button
                      onClick={() => handleStartEdit(card)}
                      style={{ ...iconButtonStyle, width: '24px', height: '24px', padding: '3px' }}
                      title={t('App.Reader.Flashcard.EditCardLabel')}
                      aria-label={t('App.Reader.Flashcard.EditCardLabel')}
                    >
                      <Edit3 className="w-3 h-3" aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => onDeleteCard(card.id)}
                      style={{ ...iconButtonStyle, width: '24px', height: '24px', padding: '3px' }}
                      title={t('App.Reader.Flashcard.DeleteCardLabel')}
                      aria-label={t('App.Reader.Flashcard.DeleteCardLabel')}
                    >
                      <Trash2 className="w-3 h-3" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      ) : (
        !isAddingCard && (
          <div
            style={{
              textAlign: 'center',
              padding: '16px 0',
              fontSize: '0.813rem',
              color: 'var(--color-text-tertiary)',
            }}
          >
            {t('App.Reader.Flashcard.NoCardsMessage')}
          </div>
        )
      )}
    </>
  );
}
